// src/features/analytics/components/ConcurrencyTrendCard.tsx

import { Paper, Skeleton, Text, Title } from '@mantine/core';
import { AreaChart } from '@mantine/charts';
import { DateTime } from 'luxon';
import type { UtcRange } from '@/shared/utils/utcRange';
import { AnalyticsCoverageNotice } from './AnalyticsCoverageNotice';
import { AnalyticsErrorState } from './AnalyticsErrorState';
import type { AnalyticsEnvelope, ConcurrencyData } from '../types';

interface ConcurrencyTrendCardProps {
  concurrency: AnalyticsEnvelope<ConcurrencyData> | null;
  range: UtcRange;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  onRetry: () => void;
}

/** Minute/hour buckets are points in time — labeled in the viewer's LOCAL timezone (ADR-003 §4). */
function bucketLabel(bucketSeconds: number, interval: 'minute' | 'hour'): string {
  const dt = DateTime.fromSeconds(bucketSeconds);
  return interval === 'hour' ? dt.toFormat('MMM d, ha') : dt.toFormat('MMM d, HH:mm');
}

export function ConcurrencyTrendCard({
  concurrency,
  range,
  isLoading,
  isError,
  error,
  onRetry,
}: ConcurrencyTrendCardProps) {
  const points = concurrency?.data.points ?? [];
  const interval = concurrency?.data.interval ?? 'hour';
  const chartData = points.map((p) => ({
    bucket: bucketLabel(p.bucket, interval),
    Peak: p.peakConcurrent,
    Average: Math.round(p.avgConcurrent * 10) / 10,
  }));

  return (
    <Paper withBorder p="md">
      <Title order={5} mb="xs">
        Concurrency trend
      </Title>
      {isLoading ? (
        <Skeleton height={220} />
      ) : isError ? (
        <AnalyticsErrorState error={error} onRetry={onRetry} />
      ) : points.length === 0 ? (
        <AnalyticsCoverageNotice
          coverage={concurrency?.coverage}
          freshness={concurrency?.freshness}
          range={range}
          emptyMessage="No concurrency data for this range."
        />
      ) : (
        <>
          <AnalyticsCoverageNotice
            coverage={concurrency?.coverage}
            freshness={concurrency?.freshness}
            range={range}
          />
          <AreaChart
            h={220}
            data={chartData}
            dataKey="bucket"
            series={[
              { name: 'Peak', color: 'blue.6' },
              { name: 'Average', color: 'cyan.4' },
            ]}
            curveType="monotone"
            withDots={false}
            withLegend
          />
          <Text size="xs" c="dimmed" mt={4}>
            {interval === 'hour' ? 'Hourly' : 'Per-minute'} peak and average concurrent
            viewers (times shown in your local timezone).
          </Text>
        </>
      )}
    </Paper>
  );
}
